import React, { useState } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';

const SEV_COLOR = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#22c55e' };
const SEV_BG    = { CRITICAL: 'rgba(239,68,68,.15)', HIGH: 'rgba(249,115,22,.15)', MEDIUM: 'rgba(234,179,8,.15)', LOW: 'rgba(34,197,94,.15)' };
const SEV_BORDER= { CRITICAL: 'rgba(239,68,68,.3)', HIGH: 'rgba(249,115,22,.3)', MEDIUM: 'rgba(234,179,8,.3)', LOW: 'rgba(34,197,94,.3)' };

function DonutScore({ score, color }) {
  const r = 52; const C = 2 * Math.PI * r;
  const offset = C - (score / 100) * C;
  return (
    <div style={{ position: 'relative', width: 140, height: 140 }}>
      <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="11" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="11"
          strokeDasharray={C} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--ff-display)', fontSize: 40, fontWeight: 800, letterSpacing: '-0.04em', color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--txt3)' }}>/100</div>
      </div>
    </div>
  );
}

function VulnCard({ v, isOpen, onToggle }) {
  const color = SEV_COLOR[v.severity] || '#9999bb';
  const bg = SEV_BG[v.severity] || 'rgba(255,255,255,.05)';
  const border = SEV_BORDER[v.severity] || 'var(--border)';
  const moduleIcon = { CipherCore: '🔐', ProbeScan: '🔍', GuardMind: '🧠' };
  return (
    <div style={{ background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--borderacc)' : border}`, borderRadius: 14, marginBottom: 10, overflow: 'hidden', transition: 'border-color .2s' }}>
      <div onClick={onToggle} style={{ padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
        <div style={{ padding: '3px 12px', borderRadius: 6, fontFamily: 'var(--ff-mono)', fontSize: 10, fontWeight: 600, letterSpacing: '.08em', background: bg, color, border: `1px solid ${border}`, flexShrink: 0, marginTop: 2 }}>
          {v.severity}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--ff-display)', fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--txt)', marginBottom: 3 }}>{v.title}</div>
          <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--txt3)' }}>{v.category} · {v.id}</div>
        </div>
        <div style={{ color: 'var(--txt3)', fontSize: 12, flexShrink: 0, marginTop: 4 }}>{isOpen ? '▲' : '▼'}</div>
      </div>
      {isOpen && (
        <div style={{ padding: '0 22px 22px', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, paddingTop: 18 }}>
          {[
            { label: 'Description', val: v.description },
            { label: 'Business Impact', val: v.impact },
            { label: 'Current Configuration', val: v.currentConfig, mono: true, warn: true },
            { label: 'Recommendation', val: v.recommendation },
          ].map(({ label, val, mono, warn }) => val && (
            <div key={label}>
              <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 13, color: warn ? color : 'var(--txt2)', fontFamily: mono ? 'var(--ff-mono)' : 'var(--ff-body)', lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
          {v.sentinelModule && (
            <div>
              <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>SentinelAI Module</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 6, background: 'rgba(124,58,237,.12)', border: '1px solid rgba(124,58,237,.25)', fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--violetbr)' }}>
                {moduleIcon[v.sentinelModule] || '🛡️'} {v.sentinelModule}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Results({ data, onReset }) {
  const [filter, setFilter] = useState('ALL');
  const [openVuln, setOpenVuln] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { riskMetrics, aiAnalysis, companyName } = data;
  const color = riskMetrics?.color || '#7c3aed';
  const vulns = aiAnalysis?.vulnerabilities || [];
  const filtered = filter === 'ALL' ? vulns : vulns.filter(v => v.severity === filter);

  const downloadPdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${base}/report-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report: data }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (companyName || 'assessment').replace(/[^a-z0-9]/gi, '_');
      a.href = url;
      a.download = `SentinelAI_Report_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not generate PDF: ' + e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const radarData = aiAnalysis?.riskByCategory
    ? Object.entries(aiAnalysis.riskByCategory).map(([k, v]) => ({
        subject: k.replace(/([A-Z])/g, ' $1').trim().replace(/^./, s => s.toUpperCase()),
        value: v
      }))
    : [];

  const kpiKeys = [
    { label: 'Data Protection', key: 'dataProtection' },
    { label: 'Model Security', key: 'modelSecurity' },
    { label: 'Agent Governance', key: 'agentGovernance' },
    { label: 'Monitoring', key: 'monitoringCompliance' },
    { label: 'Safety Controls', key: 'safetyControls' },
  ];

  const phaseColors = ['var(--red)', 'var(--orange)', 'var(--emerald)'];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(7,7,14,.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)', padding: '0 28px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
          <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em', background: 'linear-gradient(135deg,#f0f0ff,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>SentinelAI</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { label: '← New Assessment', fn: onReset, primary: false },
              { label: pdfLoading ? '⏳ Generating…' : '⬇ Download PDF', fn: downloadPdf, primary: true, disabled: pdfLoading },
            ].map(({ label, fn, primary, disabled }) => (
              <button key={label} onClick={fn} disabled={disabled} style={{
                padding: '8px 18px', borderRadius: 10,
                background: primary ? 'linear-gradient(135deg,var(--violet),#6d28d9)' : 'transparent',
                border: primary ? 'none' : '1px solid rgba(255,255,255,.12)',
                color: primary ? '#fff' : 'var(--txt2)',
                fontSize: 13, fontWeight: 600,
                cursor: disabled ? 'wait' : 'pointer',
                opacity: disabled ? 0.7 : 1,
                boxShadow: primary ? '0 0 14px rgba(124,58,237,.4)' : 'none',
                transition: 'all .2s'
              }}
                onMouseEnter={e => { if (!primary) { e.target.style.borderColor = 'var(--borderacc)'; e.target.style.color = 'var(--txt)'; } }}
                onMouseLeave={e => { if (!primary) { e.target.style.borderColor = 'rgba(255,255,255,.12)'; e.target.style.color = 'var(--txt2)'; } }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '44px 28px 80px', display: 'flex', flexDirection: 'column', gap: 22 }}>

        {/* Hero banner */}
        <div className="fadeup" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 24, padding: '40px 44px', display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 44, alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${color},var(--violet))` }} />
          <div>
            <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>STEP 2 — SECURITY INTELLIGENCE REPORT</div>
            <h1 style={{ fontFamily: 'var(--ff-display)', fontSize: 32, fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 10, color: 'var(--txt)' }}>{companyName || 'Your Organisation'}</h1>
            <p style={{ fontSize: 14, color: 'var(--txt2)', lineHeight: 1.65, maxWidth: 380 }}>{aiAnalysis?.overallVerdict}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <DonutScore score={riskMetrics?.score || 0} color={color} />
            <div style={{ padding: '8px 20px', borderRadius: 100, background: `${color}22`, border: `1px solid ${color}66`, fontFamily: 'var(--ff-display)', fontSize: 16, fontWeight: 800, color, letterSpacing: '.05em', animation: 'pulse 2s ease-in-out infinite' }}>
              {riskMetrics?.riskLevel || 'UNKNOWN'}
            </div>
          </div>
          <div>
            <p style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.7, marginBottom: 20 }}>{aiAnalysis?.executiveSummary}</p>
            {aiAnalysis?.quickWins?.length > 0 && (
              <div>
                <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--emerald)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>⚡ Quick Wins</div>
                {aiAnalysis.quickWins.slice(0, 3).map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--txt2)', padding: '5px 0', display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--emerald)', flexShrink: 0 }}>→</span>{w}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Radar + KPIs */}
        {(radarData.length > 0 || kpiKeys.length > 0) && (
          <div className="fadeup1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
            {radarData.length > 0 && (
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 18, padding: 26 }}>
                <div style={{ fontFamily: 'var(--ff-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 18 }}>Risk Radar</div>
                <ResponsiveContainer width="100%" height={230}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,.07)" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#9999bb', fontSize: 10, fontFamily: 'DM Mono' }} />
                    <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.15} />
                    <Tooltip contentStyle={{ background: 'var(--card2)', border: '1px solid var(--borderacc)', borderRadius: 8, fontFamily: 'DM Mono', fontSize: 12 }} labelStyle={{ color: 'var(--txt)' }} itemStyle={{ color }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 18, padding: 26 }}>
              <div style={{ fontFamily: 'var(--ff-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 18 }}>Category Breakdown</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {kpiKeys.map(({ label, key }) => {
                  const val = aiAnalysis?.riskByCategory?.[key] ?? 0;
                  const c = val >= 75 ? 'var(--red)' : val >= 50 ? 'var(--orange)' : val >= 25 ? 'var(--amber)' : 'var(--emerald)';
                  return (
                    <div key={key} style={{ background: 'var(--surface)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
                      <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
                      <div style={{ fontFamily: 'var(--ff-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.04em', color: c }}>{val}</div>
                      <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)' }}>/ 100</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Vulnerabilities */}
        {vulns.length > 0 && (
          <div className="fadeup2">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--ff-display)', fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em' }}>Detected Vulnerabilities</h2>
              {['CRITICAL','HIGH','MEDIUM'].map(s => {
                const cnt = vulns.filter(v => v.severity === s).length;
                return cnt > 0 ? (
                  <span key={s} style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, padding: '3px 10px', borderRadius: 100, background: SEV_BG[s], color: SEV_COLOR[s], border: `1px solid ${SEV_BORDER[s]}` }}>
                    {cnt} {s}
                  </span>
                ) : null;
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
              {['ALL','CRITICAL','HIGH','MEDIUM','LOW'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  style={{ padding: '5px 14px', borderRadius: 100, fontFamily: 'var(--ff-mono)', fontSize: 11, border: `1px solid ${filter === f ? 'rgba(124,58,237,.5)' : 'rgba(255,255,255,.1)'}`, background: filter === f ? 'rgba(124,58,237,.15)' : 'var(--surface)', color: filter === f ? 'var(--violetbr)' : 'var(--txt2)', transition: 'all .15s' }}>
                  {f}
                </button>
              ))}
            </div>
            {filtered.map((v, i) => (
              <VulnCard key={i} v={v} isOpen={openVuln === i} onToggle={() => setOpenVuln(openVuln === i ? null : i)} />
            ))}
          </div>
        )}

        {/* Roadmap */}
        {aiAnalysis?.roadmap?.length > 0 && (
          <div>
            <h2 style={{ fontFamily: 'var(--ff-display)', fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 16 }}>Remediation Roadmap</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              {aiAnalysis.roadmap.map((phase, i) => (
                <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 22, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: phaseColors[i] || 'var(--violet)' }} />
                  <div style={{ fontFamily: 'var(--ff-display)', fontSize: 14, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>{phase.phase}</div>
                  <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', marginBottom: 14 }}>Effort: {phase.effort}</div>
                  {(phase.actions || []).map((a, j) => (
                    <div key={j} style={{ fontSize: 12, color: 'var(--txt2)', padding: '5px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                      <span style={{ color: 'var(--txt3)', flexShrink: 0 }}>·</span>{a}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compliance gaps */}
        {aiAnalysis?.complianceGaps?.length > 0 && (
          <div>
            <h2 style={{ fontFamily: 'var(--ff-display)', fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 16 }}>Compliance Gaps</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {aiAnalysis.complianceGaps.map((g, i) => {
                const pc = { HIGH: 'var(--orange)', MEDIUM: 'var(--amber)', LOW: 'var(--emerald)' }[g.priority] || 'var(--txt2)';
                const pbg = { HIGH: 'rgba(249,115,22,.15)', MEDIUM: 'rgba(234,179,8,.15)', LOW: 'rgba(34,197,94,.15)' }[g.priority] || 'var(--surface)';
                return (
                  <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--violetbr)', width: 120, flexShrink: 0 }}>{g.framework}</div>
                    <div style={{ fontSize: 13, color: 'var(--txt2)', flex: 1 }}>{g.gap}</div>
                    <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, padding: '3px 10px', borderRadius: 6, background: pbg, color: pc, border: `1px solid ${pc}44` }}>{g.priority}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg,rgba(124,58,237,.15),rgba(6,182,212,.08))', border: '1px solid var(--borderacc)', borderRadius: 20, padding: '36px 40px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--ff-display)', fontSize: 26, fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 8 }}>Ready to fix these risks?</div>
          <p style={{ color: 'var(--txt2)', marginBottom: 24, fontSize: 14 }}>SentinelAI's CipherCore, ProbeScan, and GuardMind modules address every vulnerability identified above.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onReset} style={{ padding: '12px 28px', borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'var(--txt2)', fontSize: 14, fontWeight: 600 }}>
              Run Another Assessment
            </button>
            <button onClick={downloadPdf} disabled={pdfLoading} style={{ padding: '12px 28px', borderRadius: 12, background: 'linear-gradient(135deg,var(--violet),#6d28d9)', border: 'none', color: '#fff', fontFamily: 'var(--ff-display)', fontSize: 15, fontWeight: 800, boxShadow: '0 0 24px rgba(124,58,237,.4)', cursor: pdfLoading ? 'wait' : 'pointer', opacity: pdfLoading ? 0.7 : 1 }}>
              {pdfLoading ? 'Generating PDF…' : 'Download Full Report (PDF) →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
